import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RouterPath } from "../../../assets/dictionary/RouterPath";
import ResetPasswordService from "./ResetPasswordService";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Lock, AlertCircle, CheckCircle, Eye, EyeOff } from "lucide-react";
import { trackEvent } from "../../../lib/matomo";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    checks: {
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false,
    },
  });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const passwordRegex = /^(?=.*\p{Ll})(?=.*\p{Lu})(?=.*\d).{8,}$/u;
  const isPasswordValid = passwordRegex.test(password);

  useEffect(() => {
    if (!token) {
      navigate(RouterPath.LINK_NOT_VALID);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!passwordTouched) return;
    if (password.length === 0) {
      setError("");
    } else if (!isPasswordValid) {
      setError("Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.");
    } else {
      setError("");
    }
  }, [password, passwordTouched, isPasswordValid]);

  useEffect(() => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password),
    };
    const score = Object.values(checks).filter(Boolean).length;
    setPasswordStrength({ score, checks });
  }, [password]);

  const getPasswordStrengthColor = () => {
    if (passwordStrength.score <= 2) return "text-red-500";
    if (passwordStrength.score <= 3) return "text-yellow-500";
    return "text-green-500";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      setError("Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule et un chiffre.");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const payload = { new_password: password, token };
      const response = await ResetPasswordService.postResetPassword(payload);
      if (response.status === 200) {
        try { trackEvent("auth", "reset_password_success"); } catch(_) {}
        navigate(RouterPath.PASSWORD_CHANGED);
      } else {
        setError("Erreur lors de la réinitialisation du mot de passe");
        try { trackEvent("auth", "reset_password_error"); } catch(_) {}
      }
    } catch (err) {
      if (err?.response?.status === 400) {
        navigate(RouterPath.LINK_NOT_VALID);
        try { trackEvent("auth", "reset_password_invalid_link"); } catch(_) {}
      } else {
        setError(err?.response?.data?.message || "Erreur lors de la réinitialisation du mot de passe");
        try { trackEvent("auth", "reset_password_exception"); } catch(_) {}
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 app-page-gradient">
      <div className="w-full max-w-md">
        <Card className="glass-card border-muted shadow-2xl">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-2xl font-bold text-high-contrast">Réinitialiser le mot de passe</CardTitle>
            <CardDescription className="text-muted-foreground">Entrez votre nouveau mot de passe.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-high-contrast">Nouveau mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Votre nouveau mot de passe"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => setPasswordTouched(true)}
                    required
                    className="pl-10 pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    )}
                  </Button>
                </div>
                {password && (
                  <div className="space-y-2 mt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Force du mot de passe:</span>
                      <span className={`text-sm font-medium ${getPasswordStrengthColor()}`}>
                        {passwordStrength.score <= 2 ? "Faible" : passwordStrength.score <= 3 ? "Moyen" : "Fort"}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {Object.entries(passwordStrength.checks).map(([key, passed]) => (
                        <div key={key} className="flex items-center gap-2 text-sm">
                          {passed ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          )}
                          <span className={passed ? "text-green-600" : "text-red-600"}>
                            {key === 'length' && 'Au moins 8 caractères'}
                            {key === 'uppercase' && 'Au moins une majuscule'}
                            {key === 'lowercase' && 'Au moins une minuscule'}
                            {key === 'number' && 'Au moins un chiffre'}
                            {key === 'special' && 'Au moins un caractère spécial (@$!%*?&)'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Envoi en cours..." : "Réinitialiser le mot de passe"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
